import { Suspense, lazy, useEffect, useState } from 'react';
import { Outlet, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import "./App.css";
import Chat from './chat/chatscreen';
import ChatWidget from './chat/ChatWidget';
import AdminRoute from './Layout/AdminRoute';
import AuditorRoute from './Layout/AuditorRoute';
import InstitutionAccessRoute from './Layout/InstitutionAccessRoute';
import Layout from './Layout/Layout';
import PlatformAdminRoute from './Layout/PlatformAdminRoute';
import ProtectedRoutes from './Layout/ProtectedRoutes';
import ActionCards from './page/ActionCards';
import UsersAccess from './page/UsersAccess';
import AccessAdministration from './page/AccessAdministration';
import Dashboard from './page/Dashboard';
import DepartmentsAndUnits from './page/DepartmentsAndUnits';
import IncidentClassification from './page/IncidentClassification';
import IncidentDetail from './page/IncidentDetail';
const ZoomMeeting = lazy(() => import('./page/ZoomMeeting'));
import LocationsAndZones from './page/LocationsAndZones';
import Login from './page/Login';
import AcceptInvitation from './page/AcceptInvitation';
import NewIncident from './page/NewIncident';
import ScenarioLibrary from './page/ScenarioLibrary';
import ScenarioPreview from './page/ScenarioPreview';
import ScenarioMarketplace from './page/ScenarioMarketplace';
import CorrectiveActionsRegister from './page/CorrectiveActionsRegister';
import ReportsCenter from './page/ReportsCenter';
import Notifications from './page/Notifications';
import Profile from './page/Profile';
import SignUp from './page/SignUp';
import StatusPage from './page/StatusPage';
import SaaSAdministration from './page/SaaSAdministration';
import SupportAccess from './page/SupportAccess';
import TeamsAndCrews from './page/TeamsAndCrews';
import Resources from './page/Resources';
import IoTSensors from './page/IoTSensors';
import IoTDevices from './page/IoTDevices';
import ApiConnections from './page/ApiConnections';
import ForeSight from './page/ForeSight';
import WarningDetail from './page/WarningDetail';
import MapPage from './page/MapPage';
import ExerciseMapPage from './page/ExerciseMapPage';
import PartnerPage from './page/PartnerPage';
import { ZoomMeetingProvider } from './context/ZoomMeetingContext';
import ModalScrollLock from './components/ModalScrollLock';
import ZoomMeetingWidget from './components/ZoomMeetingWidget';
import { TIMELINE_RESPONDER_ACTIVITY_EVENT } from './components/timelineFocusController';

import { Toaster } from 'sonner';

// Main App Router
function App() {
  const [reportsCopilotActive, setReportsCopilotActive] = useState(false);

  useEffect(() => {
    const handleIncidentRealtimeSync = (event) => {
      const detail = event?.detail || {};
      const isResponderActivity = detail?.payload?.activity?.is_responder_activity === true;
      const isFeedOnlyActivity = ['chat_message_created', 'timeline_comment_added'].includes(detail?.change_type);
      const canChangeRelatedActivity = [
        'incident_created',
        'incident_updated',
        'incident_deleted',
        'incident_archived',
      ].includes(detail?.change_type);
      if (isResponderActivity) {
        // Focus policy lives inside the currently open incident timeline. The
        // global bridge only forwards the server-authenticated mobile event.
        window.dispatchEvent(new CustomEvent(TIMELINE_RESPONDER_ACTIVITY_EVENT, { detail }));
        window.dispatchEvent(new CustomEvent('refresh-activity-feed', { detail }));
      } else if (isFeedOnlyActivity) {
        window.dispatchEvent(new CustomEvent('refresh-activity-feed', { detail }));
        return;
      }
      if (canChangeRelatedActivity) {
        window.dispatchEvent(new CustomEvent('refresh-activity-feed', { detail }));
      }

      // If the current user just sent a status-update reply, avoid refetch flicker/reload.
      try {
        const last = window.__lastStatusUpdateReplySent;
        const itemId = detail?.payload?.timeline_item_id;
        const incidentId = detail?.incident_id;
        if (
          last &&
          incidentId &&
          String(last.incidentId) === String(incidentId) &&
          (!itemId || String(last.timelineItemId) === String(itemId)) &&
          Date.now() - Number(last.ts || 0) < 4000 &&
          !isResponderActivity
        ) {
          return;
        }
      } catch (e) {
        // ignore
      }

      // Reuse existing refresh listeners across pages/widgets without dropping
      // incident scope. Consumers coalesce these paired invalidations so one
      // socket message produces one background request per data surface.
      window.dispatchEvent(new CustomEvent('timelineRefresh', { detail }));
      window.dispatchEvent(new CustomEvent('dashboardRefresh', { detail }));
    };

    window.addEventListener('incidentRealtimeSync', handleIncidentRealtimeSync);
    return () => window.removeEventListener('incidentRealtimeSync', handleIncidentRealtimeSync);
  }, []);

  // --- Screen Capture Prevention (Best-Effort) ---
  // DISABLED FOR DEVELOPMENT: right-click, copy, print, and save shortcuts are allowed
  // so users can select and copy content. Re-enable for production if needed.
  // (See previous implementation in git history / README for the deterrent listeners.)



  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <ModalScrollLock />
      <Router>
        <ZoomMeetingProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/partner/:slug" element={<PartnerPage />} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoutes>
                <>
                  <ZoomMeetingWidget />
                  <Routes>
                    <Route
                      path="/meets"
                      element={
                        <Suspense fallback={null}>
                          <ZoomMeeting />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/*"
                      element={
                        <Layout>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/users" element={<InstitutionAccessRoute><UsersAccess /></InstitutionAccessRoute>} />
                            <Route path="/access-administration" element={<InstitutionAccessRoute><AccessAdministration /></InstitutionAccessRoute>} />
                            <Route path="/saas-administration" element={<PlatformAdminRoute><SaaSAdministration /></PlatformAdminRoute>} />
                            <Route path="/support-access" element={<SupportAccess />} />
                            <Route
                              path="/reports"
                              element={(
                                <AuditorRoute>
                                  <ReportsCenter onCopilotVisibilityChange={setReportsCopilotActive} />
                                </AuditorRoute>
                              )}
                            />
                            <Route path="/exercise-map/:id" element={<ExerciseMapPage />} />
                            <Route path="/incident-details/:id" element={<IncidentDetail />} />
                            <Route path="/incident-details/:id/chat" element={<Chat />} />
                            <Route path="/scenario-library" element={<ScenarioLibrary />} />
                            <Route path="/scenario-library/:id" element={<ScenarioPreview />} />
                            <Route path="/scenario-marketplace" element={<ScenarioMarketplace />} />

                            <Route element={<AdminRoute><Outlet /></AdminRoute>}>
                              <Route path="/new-incident" element={<NewIncident />} />
                              <Route path="/map" element={<MapPage />} />
                              <Route path="/corrective-actions" element={<CorrectiveActionsRegister />} />
                              <Route path="/incidents" element={<IncidentClassification />} />
                              <Route path="/departments" element={<DepartmentsAndUnits />} />
                              <Route path="/actionscards" element={<ActionCards />} />
                              <Route path="/teams" element={<TeamsAndCrews />} />
                              <Route path="/resources" element={<Resources />} />
                              <Route path="/iot-devices" element={<IoTDevices />} />
                              <Route path="/api-connections" element={<ApiConnections />} />
                              <Route path="/foresight" element={<ForeSight />} />
                              <Route path="/foresight/warnings/:warningId" element={<WarningDetail />} />
                              <Route path="/locations-zones" element={<LocationsAndZones />} />
                              <Route path="/iot-sensors" element={<IoTSensors />} />
                              <Route path="/library" element={<h1>Incident Library Coming Soon</h1>} />
                              <Route path="/status" element={<StatusPage />} />
                            </Route>

                            <Route path="/*" element={<Dashboard />} />
                          </Routes>
                          <ChatWidget suppressed={reportsCopilotActive} />
                        </Layout>
                      }
                    />
                  </Routes>
                </>
              </ProtectedRoutes>
            }
          />
        </Routes>
        </ZoomMeetingProvider>
      </Router>
    </div>
  );
}

export default App;
